import type { ClobClient, OrderType, SignatureTypeV2 } from '@polymarket/clob-client-v2';
import { storageKey } from '@/lib/infra/storage';

export interface PolyApiCreds {
  key: string;
  secret: string;
  passphrase: string;
}

export type PolySignatureType = 0 | 1 | 2 | 3;

export interface PolyUserConfig {
  privateKey: string; // 0x...
  signatureType: PolySignatureType;
  funder?: string;
  apiCreds?: PolyApiCreds;
  /** Public address derived from privateKey. Cached so the UI never has to display the secret. */
  signerAddress?: string;
}

const HOST = 'https://clob.polymarket.com';
const CHAIN_ID = 137;
const CLOB_TICK_SIZES = ['0.1', '0.01', '0.001', '0.0001'] as const;
type ClobTickSize = (typeof CLOB_TICK_SIZES)[number];

// ---------------------------------------------------------------------------
// Lazy-loaded dependencies — ethers (122KB gz) + @polymarket/clob-client-v2
// Only loaded when a Polymarket function is actually called (~10% of users).
// ---------------------------------------------------------------------------
type PolyDeps = Awaited<ReturnType<typeof loadPolyDeps>>;
let _polyDeps: PolyDeps | null = null;
let _polyDepsPromise: Promise<PolyDeps> | null = null;

async function loadPolyDeps() {
  // Ensure Buffer polyfill is available (required by ethers under Vite)
  if (typeof globalThis.Buffer === 'undefined') {
    const { Buffer } = await import('buffer');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (globalThis as any).Buffer = Buffer;
  }
  const [clob, eth] = await Promise.all([
    import('@polymarket/clob-client-v2'),
    import('ethers'),
  ]);
  return {
    ClobClient: clob.ClobClient,
    OrderType: clob.OrderType,
    Side: clob.Side,
    Chain: clob.Chain,
    SignatureTypeV2: clob.SignatureTypeV2,
    AssetType: clob.AssetType,
    Wallet: eth.Wallet,
  };
}

async function getPolyDeps(): Promise<PolyDeps> {
  if (_polyDeps) return _polyDeps;
  if (!_polyDepsPromise) {
    _polyDepsPromise = loadPolyDeps()
      .then((deps) => {
        _polyDeps = deps;
        return deps;
      })
      .catch((err) => {
        _polyDepsPromise = null;
        throw err;
      });
  }
  return _polyDepsPromise;
}

export function normalizePrivateKey(pk: string): string {
  const s = String(pk || '').trim();
  if (!s) return '';
  return s.startsWith('0x') ? s : `0x${s}`;
}

const EVM_ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;
const EVM_PRIVATE_KEY_RE = /^0x[0-9a-fA-F]{64}$/;
const SECP256K1_ORDER = BigInt('0xfffffffffffffffffffffffffffffffebaaedce6af48a03bbfd25e8cd0364141');

export function isValidEvmAddress(s: string): boolean {
  return EVM_ADDRESS_RE.test(String(s ?? '').trim());
}

export function isValidEvmPrivateKey(s: string): boolean {
  const key = normalizePrivateKey(String(s ?? ''));
  if (!EVM_PRIVATE_KEY_RE.test(key)) return false;
  try {
    const value = BigInt(key);
    return value > 0n && value < SECP256K1_ORDER;
  } catch {
    return false;
  }
}

export function isSupportedPolySignatureType(value: unknown): value is PolySignatureType {
  return value === 0 || value === 1 || value === 2 || value === 3;
}

export function normalizeClobTickSize(raw: unknown): ClobTickSize {
  if (raw == null) return '0.01';
  const n =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string' && raw.trim() !== ''
        ? Number(raw.trim())
        : Number.NaN;
  if (!Number.isFinite(n) || n <= 0) return '0.01';
  return CLOB_TICK_SIZES.find((tick) => Number(tick) === n) ?? '0.01';
}

export interface PolyConnectInput {
  privateKey: string;
  signatureType: number;
  funder?: string;
}

/**
 * Pre-flight validation for a Polymarket connect attempt.
 * Returns the error message, or null if valid.
 *
 * Pass `signerAddress` (derived from privateKey) to also catch the most common
 * Polymarket integration mistake: pasting the signer address as the funder for
 * non-EOA signature types. Without this guard the SDK signs orders against the
 * EOA instead of the proxy/Safe/deposit wallet that actually holds collateral.
 */
export function validatePolyConnectArgs(
  input: PolyConnectInput,
  signerAddress?: string
): string | null {
  if (!isSupportedPolySignatureType(input.signatureType)) {
    return 'Signature type must be Magic (1), Gnosis (2), or Deposit Wallet (3).';
  }
  // Mirrors the relay-side lockout: Polymarket rejects bare-EOA makers at
  // order placement ("maker address not allowed") — reads still work, so
  // an EOA connect LOOKS valid until the first order. Refuse up front.
  if (input.signatureType === 0) {
    return 'EOA (type 0) keys are no longer accepted: Polymarket rejects bare-EOA makers at order placement ("maker address not allowed"). Register the signer under the signature type + funder that match its polymarket.com account.';
  }
  if (!isValidEvmPrivateKey(input.privateKey)) {
    return 'Private key must be a valid 0x + 64-hex EVM private key.';
  }
  const funder = String(input.funder ?? '').trim();
  if (!funder) {
    return 'Funder address is required for Magic, Gnosis, and Deposit Wallet. Paste the wallet address shown on your Polymarket profile.';
  }
  if (!isValidEvmAddress(funder)) {
    return 'Funder must be a valid 0x address.';
  }
  if (signerAddress && funder.toLowerCase() === signerAddress.toLowerCase()) {
    return 'Funder must be your Polymarket proxy / Safe / deposit wallet address — not your signer address. Find it on your Polymarket profile.';
  }
  return null;
}

export async function deriveSignerAddress(privateKey: string): Promise<string> {
  const { Wallet } = await getPolyDeps();
  return new Wallet(normalizePrivateKey(privateKey)).address;
}

function polyApiErrorMessage(res: unknown): string | null {
  if (!res || typeof res !== 'object') return null;
  const rec = res as { error?: unknown; errorMsg?: unknown; status?: unknown; success?: unknown };
  if (!('error' in rec) && rec.success !== false) return null;
  const detail =
    'error' in rec
      ? typeof rec.error === 'string'
        ? rec.error
        : JSON.stringify(rec.error)
      : typeof rec.errorMsg === 'string' && rec.errorMsg
        ? rec.errorMsg
        : 'request failed';
  const status = rec.status != null ? ` ${String(rec.status)}` : '';
  return `Polymarket API${status}: ${detail}`;
}

export async function createOrDerivePolyApiCreds(privateKey: string): Promise<PolyApiCreds> {
  const { ClobClient, Chain, Wallet } = await getPolyDeps();
  const signer = new Wallet(normalizePrivateKey(privateKey));
  const temp = new ClobClient({ host: HOST, chain: Chain.POLYGON, signer });
  const creds = (await temp.createOrDeriveApiKey()) as unknown as Record<string, unknown>;
  const error = polyApiErrorMessage(creds);
  if (error) throw new Error(error);
  const normalized = {
    key: String(creds.key ?? creds.apiKey ?? ''),
    secret: String(creds.secret ?? ''),
    passphrase: String(creds.passphrase ?? ''),
  };
  if (!normalized.key || !normalized.secret || !normalized.passphrase) {
    throw new Error('Polymarket API credentials response was incomplete.');
  }
  return normalized;
}

export async function createPolyTradingClient(config: PolyUserConfig): Promise<ClobClient> {
  const { ClobClient, Chain, Wallet } = await getPolyDeps();
  const signer = new Wallet(normalizePrivateKey(config.privateKey));
  if (!config.apiCreds) {
    throw new Error('Missing Polymarket API credentials (derive first).');
  }
  // signatureType numeric values (0/1/2/3) line up with SignatureTypeV2.
  // The cast keeps the runtime integer intact while satisfying v2's enum typing.
  return new ClobClient({
    host: HOST,
    chain: Chain.POLYGON,
    signer,
    creds: config.apiCreds,
    signatureType: config.signatureType as unknown as SignatureTypeV2,
    funderAddress: config.funder,
  });
}

/**
 * Place a Polymarket order.
 *
 * @param args.tokenId - The outcome token ID (specifies WHICH outcome you're trading)
 * @param args.action - 'buy' to purchase tokens, 'sell' to sell tokens you own
 * @param args.priceCents - Limit price in cents (1-99)
 * @param args.contracts - Number of contracts/tokens
 */
export async function placePolyOrder(args: {
  client: ClobClient;
  tokenId: string;
  action: 'buy' | 'sell';
  priceCents: number;
  contracts: number;
  postOnly?: boolean;
  tickSize?: string;
  negRisk?: boolean;
}): Promise<unknown> {
  const { OrderType: OT, Side } = await getPolyDeps();
  const price = Math.max(0.0001, Math.min(0.9999, Number(args.priceCents) / 100));
  const size = Number(args.contracts);
  if (!Number.isFinite(size) || size <= 0) throw new Error('Invalid size');

  const orderSide = args.action === 'buy' ? Side.BUY : Side.SELL;
  // v2's CreateOrderOptions.tickSize is a literal union, not an open
  // string. Hydra normally forwards Gamma's per-market value, but normalize
  // at the CLOB boundary too so stale/local callers cannot pass arbitrary
  // strings into the SDK. Undefined or unrecognized values fall back to
  // 0.01, the dominant sports-moneyline/election-binary tick size.
  const options = {
    tickSize: normalizeClobTickSize(args.tickSize),
    negRisk: Boolean(args.negRisk ?? false),
  };

  const userOrder = { tokenID: String(args.tokenId), price, side: orderSide, size };

  if (args.postOnly) {
    const signed = await args.client.createOrder(userOrder, options);
    const result = await args.client.postOrder(signed, OT.GTC, true);
    const error = polyApiErrorMessage(result);
    if (error) throw new Error(error);
    return result;
  }
  const result = await args.client.createAndPostOrder(userOrder, options, OT.GTC);
  const error = polyApiErrorMessage(result);
  if (error) throw new Error(error);
  return result;
}

export interface PolyFunderBalance {
  /** Collateral balance visible to the authenticated Polymarket account, in dollars. */
  collateralUsd: number;
  /** Collateral allowance visible to the authenticated Polymarket account, in dollars. */
  collateralAllowanceUsd: number;
}

/**
 * Fetch the authenticated account's collateral balance + allowance.
 *
 * In v2, getBalanceAllowance returns `allowances: Record<string,string>`
 * (keyed by spender) instead of v1's single `allowance: string`. We
 * surface the max raw value across all spenders as the displayed
 * allowance — the highest spender's allowance is what determines
 * tradeable headroom. Treat a successful response as an L2-auth /
 * signature-type sanity check, not as proof that the user-entered funder
 * address matches the signer.
 */
export async function fetchPolyFunderBalance(client: ClobClient): Promise<PolyFunderBalance> {
  const { AssetType } = await getPolyDeps();
  const res = await client.getBalanceAllowance({ asset_type: AssetType.COLLATERAL });
  const error = polyApiErrorMessage(res);
  if (error) throw new Error(error);
  // USDC has 6 decimals on Polygon — scale base units to dollars.
  const toUsd = (raw: string | undefined): number => {
    const n = Number(raw ?? '0') / 1e6;
    return Number.isFinite(n) ? n : 0;
  };
  const allowancesRaw = res?.allowances ?? {};
  let maxRaw = '0';
  for (const v of Object.values(allowancesRaw)) {
    const n = Number(v ?? '0');
    const cur = Number(maxRaw);
    if (Number.isFinite(n) && n > cur) maxRaw = String(v);
  }
  return {
    collateralUsd: toUsd(res.balance),
    collateralAllowanceUsd: toUsd(maxRaw),
  };
}

/**
 * Cancel a single Polymarket order by ID.
 */
export async function cancelPolyOrder(client: ClobClient, orderId: string): Promise<unknown> {
  const result = await client.cancelOrder({ orderID: orderId });
  const error = polyApiErrorMessage(result);
  if (error) throw new Error(error);
  return result;
}

/**
 * Cancel all open Polymarket orders.
 */
export async function cancelAllPolyOrders(client: ClobClient): Promise<unknown> {
  const result = await client.cancelAll();
  const error = polyApiErrorMessage(result);
  if (error) throw new Error(error);
  return result;
}

const STORAGE_KEY = storageKey('polymarket_config_v1');

export function loadPolyUserConfig(): PolyUserConfig | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== 'object') return null;

    // Migrate old apiKey -> key format
    if (obj.apiCreds && typeof obj.apiCreds === 'object') {
      const creds = obj.apiCreds as Record<string, unknown>;
      if (!creds.key && creds.apiKey) {
        creds.key = creds.apiKey;
        delete creds.apiKey;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
      }
    }

    return obj as PolyUserConfig;
  } catch {
    return null;
  }
}

export function savePolyUserConfig(config: PolyUserConfig): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

export function clearPolyUserConfig(): void {
  localStorage.removeItem(STORAGE_KEY);
}
